import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getMyMailbox, listAvailableDomains, checkAvailability, applyMailbox,
  deleteMyMailbox, getWebmailSsoUrl,
  UserMailbox, MailDomain,
} from '../services/mailService';
import { usePermission } from '../hooks/usePermission';
import { useMessage } from '../contexts/MessageContext';

import { validateSession } from '../utils/sessionValidator';
import FunctionPageHeader from '../components/FunctionPageHeader';

const FUNC = 'ee_mail_my_mailbox';

const EEMailMyMailboxPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission, loading: permLoading } = usePermission();
  const { showSuccess, showApiError, showError } = useMessage();

  const hasInit = useRef(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [mailbox, setMailbox] = useState<UserMailbox | null>(null);
  const [domains, setDomains] = useState<MailDomain[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [openingWebmail, setOpeningWebmail] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(false);

  // Apply form
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [availability, setAvailability] = useState<{ available: boolean; reason?: string } | null>(null);
  const [checkingAvail, setCheckingAvail] = useState(false);

  const canCreate = hasPermission(FUNC, 'create');
  const canRead = hasPermission(FUNC, 'read');

  const load = async () => {
    setLoading(true);
    try {
      const [mb, ds] = await Promise.all([
        getMyMailbox(),
        listAvailableDomains(),
      ]);
      setMailbox(mb);
      setDomains(ds);
      // 若帳號尚未在郵件主機就緒，每 10 秒輪詢一次直到就緒
      if (mb && !mb.server_exists) {
        pollTimer.current = setTimeout(async () => {
          try {
            const refreshed = await getMyMailbox();
            setMailbox(refreshed);
          } catch { /* silent */ } // silent ok: background polling refresh, non-blocking
        }, 10000);
      }
    } catch (err) { showApiError(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!permLoading && !hasInit.current) {
      hasInit.current = true;
      validateSession(FUNC).then(v => { if (v) load(); });
    }
  }, [permLoading]);

  // #22 Layer 3 lock：租戶只會有單一指派網域 → auto-select，UI 顯 read-only badge（不給選）
  useEffect(() => {
    if (domains.length === 1 && !selectedDomain) {
      setSelectedDomain(domains[0].domain_code);
    }
  }, [domains, selectedDomain]);

  // 清除輪詢 timer
  useEffect(() => {
    if (mailbox?.server_exists && pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, [mailbox?.server_exists]);

  useEffect(() => {
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []);

  const handleCheckAvailability = async () => {
    if (!selectedDomain || !username.trim()) return;
    setCheckingAvail(true);
    setAvailability(null);
    try {
      const r = await checkAvailability(selectedDomain, username.trim());
      setAvailability(r);
    } catch (err) { showApiError(err); }
    finally { setCheckingAvail(false); }
  };

  const handleApply = async () => {
    if (!selectedDomain || !username.trim() || !password) {
      showError('ERR020006', { field: t('common.required'), detail: '' });
      return;
    }
    setApplying(true);
    try {
      const mb = await applyMailbox({ domain: selectedDomain, username: username.trim(), current_password: password });
      setMailbox(mb);
      setShowApplyForm(false);
      showSuccess('SYS020001', { name: mb.email_address });
    } catch (err) { showApiError(err); }
    finally { setApplying(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('mail.myMailbox.confirmDelete'))) return;
    setDeleting(true);
    try {
      await deleteMyMailbox();
      setMailbox(null);
      showSuccess('SYS020001', { name: t('mail.myMailbox.title') });
    } catch (err) { showApiError(err); }
    finally { setDeleting(false); }
  };

  const handleCheckSetupProgress = async () => {
    setCheckingSetup(true);
    try {
      const mb = await getMyMailbox();
      setMailbox(mb);
      if (mb?.server_exists) {
        showSuccess('SYS020001', { name: t('mail.myMailbox.title') });
      } else {
        showError('ERR020006', { field: t('mail.myMailbox.settingUpTitle'), detail: t('mail.myMailbox.stillSettingUp') });
      }
    } catch (err) { showApiError(err); }
    finally { setCheckingSetup(false); }
  };

  const handleOpenWebmail = async () => {
    setOpeningWebmail(true);
    // 先同步開窗（在 user click context 內），避免 popup blocker 攔截
    const win = window.open('', '_blank');
    try {
      const url = await getWebmailSsoUrl();
      if (win) win.location.href = url;
    } catch (err) {
      if (win) win.close();
      showApiError(err);
    }
    finally { setOpeningWebmail(false); }
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', boxSizing: 'border-box',
  };

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;

  return (
    <div>
      <FunctionPageHeader funcCode={FUNC} />
      <div style={{ padding: '16px 24px', maxWidth: 560 }}>

        {mailbox ? (
          <div>
            <div style={{ background: '#f0f7ff', border: '1px solid #b3d4f5', borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>📧 {mailbox.email_address}</div>
              <table style={{ fontSize: 14, borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ color: '#555', paddingRight: 16, paddingBottom: 6 }}>{t('mail.myMailbox.quota')}</td>
                    <td style={{ paddingBottom: 6 }}>{mailbox.quota ?? t('common.unlimited')}</td>
                  </tr>
                  <tr>
                    <td style={{ color: '#555', paddingRight: 16, paddingBottom: 6 }}>{t('mail.myMailbox.quotaUsed')}</td>
                    <td style={{ paddingBottom: 6 }}>
                      {mailbox.quota_used ?? '-'}
                      {mailbox.quota_pct != null && ` (${mailbox.quota_pct}%)`}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: '#555', paddingRight: 16 }}>{t('mail.myMailbox.status')}</td>
                    <td style={{ color: mailbox.is_active ? '#388e3c' : '#d32f2f' }}>
                      {mailbox.is_active ? t('common.active') : t('common.inactive')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleOpenWebmail}
                disabled={openingWebmail || !mailbox.server_exists}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: 4, color: '#fff',
                  background: mailbox.server_exists ? '#1976d2' : '#bdbdbd',
                  cursor: mailbox.server_exists ? 'pointer' : 'not-allowed',
                }}>
                {openingWebmail ? t('common.loading') : t('mail.myMailbox.openWebmail')}
              </button>
              {!mailbox.server_exists && (
                <button onClick={handleCheckSetupProgress} disabled={checkingSetup}
                  style={{ padding: '8px 20px', border: '1px solid #f57c00', borderRadius: 4, color: '#f57c00', background: 'none', cursor: 'pointer' }}>
                  {checkingSetup ? t('common.loading') : t('mail.myMailbox.checkSetupProgress')}
                </button>
              )}
              <button onClick={handleDelete}
                disabled={deleting || !mailbox.server_exists}
                style={{
                  padding: '8px 20px', border: 'none', borderRadius: 4, color: '#fff',
                  background: mailbox.server_exists ? '#d32f2f' : '#bdbdbd',
                  cursor: mailbox.server_exists ? 'pointer' : 'not-allowed',
                }}>
                {deleting ? t('common.loading') : t('mail.myMailbox.deleteMailbox')}
              </button>
            </div>
            {!mailbox.server_exists && (
              <div style={{ marginTop: 12, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 18 }}>⏳</span>
                  <span style={{ fontWeight: 600, color: '#e65100' }}>{t('mail.myMailbox.settingUpTitle')}</span>
                </div>
                <div style={{ fontSize: 13, color: '#bf360c', lineHeight: 1.6 }}>
                  {t('mail.myMailbox.newAccountNotice')}
                </div>
              </div>
            )}
          </div>
        ) : domains.length === 0 ? (
          /* #22 Layer 3：租戶未指派郵件網域 → 功能鎖死，完全不顯申請表單 */
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#666' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
            <div style={{ fontWeight: 600, fontSize: 16, color: '#444', marginBottom: 8 }}>
              {t('mail.myMailbox.lockedTitle', '此功能目前無法使用')}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.7 }}>
              <div>{t('mail.myMailbox.lockedNoDomain', '您的租戶尚未指派郵件網域，請聯絡系統管理員')}</div>
              <div style={{ color: '#999' }}>
                {t('mail.myMailbox.lockedHint', '待管理員完成租戶設定作業指派網域後，即可申請')}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: '#555', marginBottom: 16 }}>{t('mail.myMailbox.noMailbox')}</p>
            {canCreate && !showApplyForm && (
              <button onClick={() => setShowApplyForm(true)}
                style={{ padding: '8px 20px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                + {t('mail.myMailbox.apply')}
              </button>
            )}
            {showApplyForm && (
              <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 20, background: '#fafafa' }}>
                <h3 style={{ margin: '0 0 16px' }}>{t('mail.myMailbox.applyForm')}</h3>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>{t('mail.domains.domainCode')}</label>
                  {/* #22 Layer 3 lock：1=租戶鎖死 read-only badge（不給選）/ 2+=admin pool dropdown。
                      length=0（未指派）由外層鎖死空狀態處理，表單根本不渲染。 */}
                  {domains.length === 1 ? (
                    <div style={{ padding: '6px 0' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: 4,
                        background: '#e3f2fd', color: '#1565c0', fontWeight: 500,
                      }}>@{domains[0].domain_code}</span>
                    </div>
                  ) : (
                    <select value={selectedDomain} onChange={e => { setSelectedDomain(e.target.value); setAvailability(null); }}
                      style={{ ...inputStyle, width: 'auto', minWidth: 200 }}>
                      <option value="">{t('common.pleaseSelect')}</option>
                      {domains.map(d => <option key={d.domain_code} value={d.domain_code}>{d.domain_code}</option>)}
                    </select>
                  )}
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>{t('mail.myMailbox.username')}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={username} onChange={e => { setUsername(e.target.value); setAvailability(null); }}
                      placeholder={t('mail.myMailbox.usernamePlaceholder')}
                      style={{ ...inputStyle, flex: 1 }} />
                    {selectedDomain && <span style={{ padding: '8px 0', color: '#555' }}>@{selectedDomain}</span>}
                    <button onClick={handleCheckAvailability} disabled={checkingAvail || !selectedDomain || !username.trim()}
                      style={{ padding: '8px 14px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      {t('mail.myMailbox.checkAvailability')}
                    </button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {availability ? (
                      <span style={{ color: availability.available ? '#388e3c' : '#d32f2f' }}>
                        {availability.available ? `✓ ${t('mail.myMailbox.available')}` : `✗ ${availability.reason}`}
                      </span>
                    ) : (
                      <span style={{ color: '#888' }}>↑ {t('mail.myMailbox.checkFirst')}</span>
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontWeight: 500, marginBottom: 4 }}>{t('mail.myMailbox.currentPassword')}</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    style={inputStyle} placeholder={t('mail.myMailbox.passwordHint')} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={handleApply} disabled={applying || !availability?.available}
                    style={{
                      padding: '8px 20px', border: 'none', borderRadius: 4,
                      background: availability?.available ? '#1976d2' : '#ccc',
                      color: '#fff',
                      cursor: availability?.available ? 'pointer' : 'not-allowed',
                      opacity: applying ? 0.7 : 1,
                    }}>
                    {applying ? t('common.loading') : t('mail.myMailbox.confirmApply')}
                  </button>
                  <button onClick={() => setShowApplyForm(false)}
                    style={{ padding: '8px 16px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}>
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EEMailMyMailboxPage;
